import { Card } from '@/components/common/card'
import { NetworkImage } from '@/components/common/networkImage'
import { ProtocolImage } from '@/components/common/protocolImage'
import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableRowLink } from '@/components/common/table'
import { ArrowIcon } from '@/components/icons/arrow'
import { InfoIcon } from '@/components/icons/info'
import { routes } from '@/config/routes'
import {
  type BorrowPosition,
  type BorrowPositionAssetBalance,
  getBorrowPositionsTotals,
} from '@/queries/borrowPositions.query'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { formatAmount, formatDecimalToPercentage, formatPreciseTokenValue, formatUSD } from '@vaultsfyi/common/utils'
import type { ReactNode } from 'react'
import { Link, generatePath } from 'react-router'

interface BorrowPositionsPanelProps {
  borrowPositions: BorrowPosition[]
}

interface BorrowPositionRowProps {
  position: BorrowPosition
}

interface BorrowPositionLabelProps extends BorrowPositionRowProps {
  className?: string
  iconClassName?: string
  networkIconClassName?: string
  contentClassName?: string
}

type BorrowAssetSide = 'collateral' | 'borrowed'

const LIQUIDATION_RISK_HEALTH_FACTOR = 1

const formatOptionalPercentage = (value: number | undefined) => {
  return value === undefined ? '-' : formatDecimalToPercentage(value)
}

const formatHealthFactor = (healthFactor: number | undefined) => {
  return healthFactor === undefined
    ? '-'
    : formatAmount(healthFactor, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const getAssetNativeValue = (value: string, decimals: number, symbol: string) => {
  return `${formatPreciseTokenValue({ value, decimals })} ${symbol}`
}

const getPositionAssetsBySide = (position: BorrowPosition, side: BorrowAssetSide) => {
  return position.assets.filter((asset) => {
    if (side === 'collateral') {
      return asset.collateralEnabled && asset.suppliedUsd > 0
    }

    return asset.borrowedUsd > 0
  })
}

const getAssetUsdTotal = (assets: BorrowPositionAssetBalance[], side: BorrowAssetSide) => {
  return assets.reduce((sum, asset) => sum + (side === 'collateral' ? asset.suppliedUsd : asset.borrowedUsd), 0)
}

const isPositionAtLiquidationRisk = (position: BorrowPosition) => {
  return position.healthFactor !== undefined && position.healthFactor <= LIQUIDATION_RISK_HEALTH_FACTOR
}

const getRiskColor = (healthFactor: number | undefined) => {
  if (healthFactor === undefined) return 'text-heading-foreground'
  if (healthFactor <= LIQUIDATION_RISK_HEALTH_FACTOR) return 'text-destructive'
  if (healthFactor < 1.5) return 'text-warning'
  return 'text-success'
}

const getPositionProtocolLabel = (position: BorrowPosition) => {
  const displayName = getProtocolDisplayName(position.protocol)
  return position.protocol.version ? `${displayName} ${position.protocol.version}` : displayName
}

const getBorrowPositionPagePath = (position: BorrowPosition) => {
  const borrowedAsset = getPositionAssetsBySide(position, 'borrowed')[0]
  if (!borrowedAsset) return undefined

  return generatePath(routes.borrowMarket, {
    network: position.network.name,
    marketId: position.marketId,
    assetId: borrowedAsset.marketAssetId,
  })
}

const BorrowPositionLabel = ({
  position,
  className,
  iconClassName = 'size-8',
  networkIconClassName = 'size-4',
  contentClassName,
}: BorrowPositionLabelProps) => {
  const positionPagePath = getBorrowPositionPagePath(position)
  const label = getPositionProtocolLabel(position)

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div className="relative shrink-0">
        <ProtocolImage protocol={position.protocol} className={cn(iconClassName, 'p-1')} />
        <NetworkImage
          network={position.network.name}
          className={cn('absolute right-0 bottom-0 p-px', networkIconClassName)}
        />
      </div>
      <div className={cn('min-w-0', contentClassName)}>
        {positionPagePath ? (
          <Link
            to={positionPagePath}
            prefetch="intent"
            className="block truncate font-medium hocus:text-accent text-heading-foreground text-sm outline-none transition-colors first-letter:uppercase before:absolute before:inset-0"
          >
            {label}
          </Link>
        ) : (
          <p className="truncate font-medium text-heading-foreground text-sm first-letter:uppercase">{label}</p>
        )}
        <p className="text-body-foreground text-xs">
          on <span className="capitalize">{getNetworkName(position.network.name)}</span>
        </p>
      </div>
    </div>
  )
}

const AmountStack = ({
  primary,
  secondary,
  className,
}: {
  primary: ReactNode
  secondary?: ReactNode
  className?: string
}) => {
  return (
    <div className={cn('flex flex-col', className)}>
      <p className="font-medium text-heading-foreground text-sm tabular-nums">{primary}</p>
      {secondary && <p className="text-body-foreground text-xs tabular-nums">{secondary}</p>}
    </div>
  )
}

const PositionAssetAmount = ({
  assets,
  side,
  className,
}: {
  assets: BorrowPositionAssetBalance[]
  side: BorrowAssetSide
  className?: string
}) => {
  const primaryAsset = assets[0]
  if (!primaryAsset) {
    return <AmountStack primary="-" className={cn('items-end', className)} />
  }

  const nativeValue = side === 'collateral' ? primaryAsset.suppliedNative : primaryAsset.borrowedNative
  const usdValue = getAssetUsdTotal(assets, side)
  const additionalAssetsCopy = assets.length > 1 ? ` +${assets.length - 1}` : ''

  return (
    <AmountStack
      primary={`${getAssetNativeValue(nativeValue, primaryAsset.asset.decimals, primaryAsset.asset.symbol)}${additionalAssetsCopy}`}
      secondary={`≈ ${formatUSD(usdValue)}`}
      className={cn('items-end', className)}
    />
  )
}

const BorrowPositionRisk = ({ position, className }: BorrowPositionRowProps & { className?: string }) => {
  const hasDebt =
    position.healthFactor !== undefined || position.assets.some((asset) => BigInt(asset.borrowedNative) > 0n)

  if (!hasDebt) {
    return <AmountStack primary="-" className={cn('items-end', className)} />
  }

  return (
    <AmountStack
      primary={
        <span className={getRiskColor(position.healthFactor)}>HF {formatHealthFactor(position.healthFactor)}</span>
      }
      secondary={`LTV ${formatOptionalPercentage(position.ltv)}`}
      className={cn('items-end', className)}
    />
  )
}

const BorrowApyValue = () => {
  return (
    <div className="flex items-center justify-end gap-1">
      <p className="font-medium text-heading-foreground text-sm tabular-nums">-</p>
    </div>
  )
}

const BorrowPositionRow = ({ position }: BorrowPositionRowProps) => {
  const collateralAssets = getPositionAssetsBySide(position, 'collateral')
  const borrowedAssets = getPositionAssetsBySide(position, 'borrowed')
  const positionPagePath = getBorrowPositionPagePath(position)

  return (
    <TableRow
      data-row="position"
      className="relative transition-colors even:bg-page [[data-row=position]+&>td]:border-t"
    >
      <TableCell className="h-18 max-w-32">
        <BorrowPositionLabel position={position} />
      </TableCell>
      <TableCell className="text-right">
        <PositionAssetAmount assets={collateralAssets} side="collateral" />
      </TableCell>
      <TableCell className="text-right">
        <PositionAssetAmount assets={borrowedAssets} side="borrowed" />
      </TableCell>
      <TableCell className="text-right">
        <BorrowPositionRisk position={position} />
      </TableCell>
      <TableCell className="w-28 text-right">
        <BorrowApyValue />
      </TableCell>
      <TableCell className="!pl-1 !pr-3 w-10">
        {positionPagePath && (
          <TableRowLink
            to={positionPagePath}
            title={getPositionProtocolLabel(position)}
            prefetch="intent"
            className="focus-visible:before:rounded-xl focus-visible:before:ring focus-visible:before:ring-inset"
          >
            <ArrowIcon />
          </TableRowLink>
        )}
      </TableCell>
    </TableRow>
  )
}

const BorrowPositionsLiquidationWarning = ({ borrowPositions }: BorrowPositionsPanelProps) => {
  const positionsAtRisk = borrowPositions.filter(isPositionAtLiquidationRisk).length
  if (positionsAtRisk === 0) return null

  const positionCopy = positionsAtRisk === 1 ? 'position' : 'positions'

  return (
    <div className="flex items-center gap-1 rounded-full border border-border bg-surface px-2 py-1 text-destructive">
      <InfoIcon size={16} className="size-3.5" />
      <span className="font-medium text-xs">
        {positionsAtRisk} {positionCopy} at risk of liquidation
      </span>
    </div>
  )
}

const BorrowPositionsHeader = ({ borrowPositions }: BorrowPositionsPanelProps) => {
  const totals = getBorrowPositionsTotals(borrowPositions)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        <h2 className="font-medium text-heading-foreground text-lg">Borrow positions</h2>
        <BorrowPositionsLiquidationWarning borrowPositions={borrowPositions} />
      </div>
      <p className="font-medium text-body-foreground text-sm">
        Total borrowed: <span className="text-heading-foreground tabular-nums">{formatUSD(totals.borrowedUsd)}</span>
      </p>
    </div>
  )
}

export function BorrowPositionsPanel({ borrowPositions }: BorrowPositionsPanelProps) {
  return (
    <section className="relative flex h-fit flex-col gap-5">
      <BorrowPositionsHeader borrowPositions={borrowPositions} />
      <Card className="relative h-fit overflow-hidden p-0">
        <ScrollArea type="auto" className="w-full">
          <ScrollBar orientation="horizontal" className="top-0" />
          <Table className="min-w-[640px]">
            <TableHeader>
              <TableRow variant="header">
                <TableHead>Protocol</TableHead>
                <TableHead className="text-right">Collateral</TableHead>
                <TableHead className="text-right">Borrowed</TableHead>
                <TableHead className="text-right">Risk</TableHead>
                <TableHead className="w-28 text-right">Borrow APY</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {borrowPositions.length > 0 ? (
                borrowPositions.map((position) => (
                  <BorrowPositionRow key={`${position.network.name}-${position.marketId}`} position={position} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="py-5 text-center">
                    <p className="font-semibold text-base">No borrow positions</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </Card>
    </section>
  )
}

function StatCell({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={cn('flex xs:flex-col xs:justify-normal justify-between gap-2 xs:gap-0.5', className)}>
      <p className="text-body-foreground text-xs">{label}</p>
      {children}
    </div>
  )
}

const BorrowPositionCard = ({ position }: BorrowPositionRowProps) => {
  const collateralAssets = getPositionAssetsBySide(position, 'collateral')
  const borrowedAssets = getPositionAssetsBySide(position, 'borrowed')

  return (
    <Card className="relative flex flex-col gap-4 rounded-2xl p-4 transition-colors">
      <BorrowPositionLabel
        position={position}
        iconClassName="size-10"
        networkIconClassName="size-5"
        contentClassName="flex-1"
      />
      <div className="grid grid-cols-1 xs:grid-cols-4 gap-2 xs:gap-3">
        <StatCell label="Collateral">
          <PositionAssetAmount assets={collateralAssets} side="collateral" className="xs:items-start items-end" />
        </StatCell>
        <StatCell label="Borrowed" className="xs:mx-auto xs:w-fit">
          <PositionAssetAmount assets={borrowedAssets} side="borrowed" className="xs:items-start items-end" />
        </StatCell>
        <StatCell label="Risk" className="xs:mx-auto xs:w-fit">
          <BorrowPositionRisk position={position} className="xs:items-start items-end" />
        </StatCell>
        <StatCell label="Borrow APY" className="xs:items-end">
          <BorrowApyValue />
        </StatCell>
      </div>
    </Card>
  )
}

export function BorrowPositionsContent({ borrowPositions }: BorrowPositionsPanelProps) {
  return (
    <section className="flex flex-col gap-3 px-4 pb-4 md:px-5 md:pb-5">
      <BorrowPositionsHeader borrowPositions={borrowPositions} />
      <ul className="flex flex-col gap-2">
        {borrowPositions.map((position) => (
          <li key={`${position.network.name}-${position.marketId}`}>
            <BorrowPositionCard position={position} />
          </li>
        ))}
      </ul>
    </section>
  )
}
